// ---------------------------------------------------------------------------
// ChannelsFilterRow - Shared filter row for Cards and Table views.
// ---------------------------------------------------------------------------

import React from 'react';
import { Search, Sparkles } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { ChannelsViewModeToggle } from './ChannelsViewModeToggle';
import type { ViewMode } from '@/components/v11/discovery/types';

export interface ChannelsFilterRowProps {
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly platformFilter: string;
  readonly onPlatformFilterChange: (value: string) => void;
  readonly platforms: readonly string[];
  readonly statusFilter: string;
  readonly onStatusFilterChange: (value: string) => void;
  readonly includeOwnChannels: boolean;
  readonly onIncludeOwnChannelsChange: (value: boolean) => void;
  readonly viewMode: ViewMode;
  readonly onViewModeChange: (mode: ViewMode) => void;
}

export function ChannelsFilterRow({
  search,
  onSearchChange,
  platformFilter,
  onPlatformFilterChange,
  platforms,
  statusFilter,
  onStatusFilterChange,
  includeOwnChannels,
  onIncludeOwnChannelsChange,
  viewMode,
  onViewModeChange,
}: ChannelsFilterRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <span className="inline-flex items-center gap-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-[10px] font-medium px-2 py-0.5">
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          Period filter applies to THIS zone only
        </span>
      </div>

      <div data-testid="channels-filter-row" className="flex flex-wrap items-center gap-3">
        <div data-filter-slot className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-v11-text-muted" />
          <input
            type="text"
            placeholder="Search channels..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-v11-border bg-v11-surface text-sm text-v11-text-primary placeholder:text-v11-text-muted focus:outline-none focus:ring-1 focus:ring-v11-accent"
          />
        </div>

        <div data-filter-slot>
          <select
            aria-label="Platforms filter"
            value={platformFilter}
            onChange={e => onPlatformFilterChange(e.target.value)}
            className="min-w-[180px] rounded-lg border border-v11-border bg-v11-surface pl-3 pr-8 py-2 text-sm text-v11-text-primary focus:outline-none focus:ring-1 focus:ring-v11-accent"
          >
            <option value="all">All Platforms</option>
            {platforms.map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div data-filter-slot>
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={e => onStatusFilterChange(e.target.value)}
            className="min-w-[180px] rounded-lg border border-v11-border bg-v11-surface pl-3 pr-8 py-2 text-sm text-v11-text-primary focus:outline-none focus:ring-1 focus:ring-v11-accent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
        </div>

        <div data-filter-slot className="flex items-center gap-1.5">
          <Switch
            id="channels-include-own-channels"
            checked={includeOwnChannels}
            onCheckedChange={onIncludeOwnChannelsChange}
            className="scale-75"
          />
          <span
            className="text-xs text-v11-text-secondary cursor-pointer select-none"
            onClick={() => onIncludeOwnChannelsChange(!includeOwnChannels)}
          >
            Show Own Channels
          </span>
        </div>

        <div data-filter-slot className="ml-auto pl-5">
          <ChannelsViewModeToggle value={viewMode} onChange={onViewModeChange} />
        </div>
      </div>
    </div>
  );
}
